// RunChrome.js
// The React half of the shared run display vocabulary in `utils/runDisplay.js`:
// the origin icon and the outcome glyph. Both are used by the Dashboard run
// picker, the Activity feed and the provenance line on every View widget, so
// they live in one place and never drift.

import React from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { outcomeDisplay, runOriginLabel } from '../../utils/runDisplay';

const ORIGIN_ICONS = {
  manual: PersonIcon,
  view: DashboardIcon,
  agent: SmartToyIcon,
  on_new_data: StorageIcon,
  on_schedule: ScheduleIcon,
};

// An unknown trigger_type still renders an icon (the neutral person glyph) but
// its label — supplied by the caller from `runOriginLabel` — stays verbatim.
// `className` is forwarded because MUI clones this element into slots that style
// it that way — a Chip's `icon` prop being the common one.
export function RunOriginIcon({ triggerType, fontSize = 13, sx = {}, title = null, className }) {
  const Icon = ORIGIN_ICONS[triggerType] || PersonIcon;
  const icon = <Icon className={className} sx={{ fontSize, ...sx }} />;
  if (!title) return icon;
  return <Tooltip title={title}>{icon}</Tooltip>;
}

const TONE_COLORS = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  info: 'info.main',
  default: 'text.disabled',
};

// The compact ✓ / ⚠ / ✕ mark. Queued and running runs show a spinner instead,
// because those are not outcomes yet.
export function RunOutcomeGlyph({ outcome, fontSize = 13, sx = {} }) {
  const display = outcomeDisplay(outcome);
  if (display.spinner) {
    return (
      <Tooltip title={display.label}>
        <CircularProgress size={fontSize} sx={{ ...sx }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={display.label}>
      <Box
        component="span"
        sx={{
          fontSize: `${fontSize}px`,
          fontWeight: 700,
          lineHeight: 1,
          color: TONE_COLORS[display.tone] || TONE_COLORS.default,
          ...sx,
        }}
      >
        {display.glyph}
      </Box>
    </Tooltip>
  );
}

export { runOriginLabel };
